import dayjs from "dayjs";
import styled from "styled-components";

import { ProjectionCalculationModal } from "@components/ProjectedChart";
import { ProjectedChartProvider } from "@contexts";
import { BeneficiaryType, Flow, Routes, SipFrequency } from "@ctypes";
import { useGoalsContext, useNavigateToStep, useProjectedChart, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { goalTypeLabel } from "@routes/GoalTypeRoute/content";
import { formatCurrency } from "@utils";
import { ArtPanel, FilledButton } from "./styles";
import { home } from "./tokens";

const CHART_W = 660;
const CHART_H = 164;
const CHART_TOP = 5;
const CHART_BASE = 155;
const CHART_POINTS = 11;
const GRID_LINES = [8, 57, 106, 155];

const Card = styled(ArtPanel)`
  background:
    radial-gradient(560px 380px at 10% -4%, rgba(143, 191, 176, 0.34), transparent 64%),
    radial-gradient(520px 420px at 96% 102%, rgba(224, 155, 124, 0.22), transparent 64%),
    linear-gradient(158deg, #1b2731 0%, #10141c 100%);
  padding: 28px 30px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Switcher = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: ${home.fill06};
`;

const SwitcherPill = styled.button<{ $active: boolean }>`
  height: 30px;
  display: flex;
  align-items: center;
  padding: 0 15px;
  border: none;
  border-radius: 999px;
  font-family: ${home.sans};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ $active }) => ($active ? home.fill14 : "transparent")};
  color: ${({ $active }) => ($active ? home.ink : home.ink50)};
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${home.line12};
  background: ${home.fill04};
  font-family: ${home.sans};
  font-size: 12px;
  font-weight: 500;
  color: ${home.ink80};
`;

const ChipButton = styled(Chip).attrs({ as: "button", type: "button" })`
  cursor: pointer;
  transition:
    background 160ms ease,
    border-color 160ms ease;

  &:hover:not(:disabled) {
    background: ${home.fill08};
    border-color: ${home.line20};
  }

  &:disabled {
    cursor: default;
  }
`;

/** Up/down arrow pair signalling the chip cycles through goals. */
const CycleIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ stroke: home.ink52 }}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 9l4-5 4 5" />
    <path d="M8 15l4 5 4-5" />
  </svg>
);

const GoalMark = styled.span`
  width: 15px;
  height: 15px;
  border-radius: 999px;
  background: ${home.copperFill};
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    width: 7px;
    height: 4px;
    border-radius: 0 0 7px 7px;
    background: ${home.copper};
    display: block;
  }
`;

const OnTrackLabel = styled.div`
  font-size: 13px;
  color: ${home.ink52};
`;

const HeadlineRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const Headline = styled.div`
  font-family: ${home.serif};
  font-size: 56px;
  line-height: 0.94;
  letter-spacing: -1.5px;
  color: ${home.ink};

  @media (max-width: 720px) {
    font-size: 40px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
  padding: 64px 24px;

  .empty-title {
    font-family: ${home.serif};
    font-size: 26px;
    color: ${home.ink};
  }

  .empty-body {
    font-size: 14px;
    color: ${home.ink52};
    max-width: 300px;
    line-height: 1.5;
  }
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 18px;
  font-size: 12px;
  color: ${home.ink52};

  .item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  .invested {
    background: ${home.ink58};
  }

  .returns {
    background: ${home.copper};
  }

  b {
    font-weight: 600;
    color: ${home.ink};
  }

  .time b {
    color: ${home.copper};
  }
`;

const ChartBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AxisChip = styled.div`
  height: 26px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  background: ${home.fill08};
  font-size: 11px;
  font-weight: 500;
  color: ${home.ink72};
  flex-shrink: 0;
`;

const displayName = (
  type: BeneficiaryType | null | undefined,
  firstName: string | null | undefined,
) => (type === BeneficiaryType.Self || !firstName ? i18n.t(Translations.HomeYou) : firstName);

export const ProjectionCard = () => {
  const navigate = useNavigateToStep();
  const { user } = useUser();
  const {
    beneficiary,
    goal,
    multiplier,
    targetYear,
    contributeAfter18,
    annualDeposit,
    initialBalance,
  } = useGoalsContext();

  const birthDate =
    beneficiary?.type === BeneficiaryType.Self ? user?.dateOfBirth : beneficiary?.dateOfBirth;

  const currentAge = birthDate ? dayjs().diff(dayjs(birthDate), "year") : 0;
  const yearsRemaining = Math.max(targetYear - dayjs().year(), 1);

  // A beneficiary without a goal yet has nothing to project – offer the
  // setup path instead of a scaffold of zeros.
  if (!goal) {
    return (
      <Card>
        <EmptyState>
          <div className="empty-title">{i18n.t(Translations.HomeNoGoalTitle)}</div>
          <div className="empty-body">{i18n.t(Translations.HomeNoGoalBody)}</div>
          <FilledButton onClick={() => navigate(Routes.AddBeneficiaryType, { state: { flow: Flow.AddGoal } })}>
            {i18n.t(Translations.HomeNoGoalCTA)}
          </FilledButton>
        </EmptyState>
      </Card>
    );
  }

  // The provider re-runs the projection whenever the calculation modal
  // adjusts assumptions, so the chart updates live before editGoal's
  // refetch settles.
  return (
    <ProjectedChartProvider
      currentAge={currentAge}
      projectedAge={currentAge + yearsRemaining}
      recurringAmount={annualDeposit}
      frequency={SipFrequency.Annually}
      multiplier={multiplier}
      contributeAfter18={contributeAfter18}
      initialBalance={initialBalance}
    >
      <ProjectionCardContent />
      <ProjectionCalculationModal />
    </ProjectedChartProvider>
  );
};

const ProjectionCardContent = () => {
  const { beneficiaries } = useUser();
  const {
    beneficiary,
    beneficiaryId,
    setBeneficiaryId,
    goal,
    goalType,
    goalName,
    goalIndex,
    setGoalId,
  } = useGoalsContext();

  const { projected, investedAmount, projectedAmount, targetYear, interest, setIsInfoVisible } =
    useProjectedChart();

  const timeAdds = Math.max(projectedAmount - investedAmount, 0);

  const numOfGoals = beneficiary?.goals?.length ?? 0;

  const toggleGoal = () => {
    if (numOfGoals <= 1) return;

    const newGoalId = beneficiary?.goals?.[(goalIndex + 1) % numOfGoals]?.id;

    if (newGoalId) {
      setGoalId(newGoalId);
    }
  };

  const chartPath = (() => {
    if (projected.length < 2) return null;

    const max = projectedAmount || 1;
    const step = (projected.length - 1) / (CHART_POINTS - 1);
    const points = Array.from({ length: CHART_POINTS }, (_, i) => {
      // Interpolate between yearly data points so short-horizon goals
      // (few points) render a slope, not a staircase.
      const position = i * step;
      const lower = Math.floor(position);
      const upper = Math.min(lower + 1, projected.length - 1);
      const fraction = position - lower;

      const amount =
        (projected[lower]?.amount ?? 0) * (1 - fraction) +
        (projected[upper]?.amount ?? 0) * fraction;

      const x = (i / (CHART_POINTS - 1)) * CHART_W;
      const y = CHART_BASE - (amount / max) * (CHART_BASE - CHART_TOP);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const line = `M${points.join(" L")}`;
    return {
      line,
      area: `${line} L${CHART_W},${CHART_BASE} L0,${CHART_BASE} Z`,
    };
  })();

  const name = displayName(beneficiary?.type, beneficiary?.firstName);

  return (
    <Card>
      <HeaderRow>
        {beneficiaries && beneficiaries.length > 1 && (
          <Switcher>
            {beneficiaries.map((b) => (
              <SwitcherPill
                key={b?.id}
                $active={b?.id === beneficiaryId}
                onClick={() => b?.id && setBeneficiaryId(b.id)}
              >
                {displayName(b?.type, b?.firstName)}
              </SwitcherPill>
            ))}
          </Switcher>
        )}

        <Chips>
          {goal && (
            <ChipButton
              style={{ paddingLeft: 9, gap: 7, paddingRight: 13 }}
              onClick={toggleGoal}
              disabled={numOfGoals <= 1}
              aria-label={i18n.t(Translations.HomeSwitchGoal)}
            >
              <GoalMark>
                <span />
              </GoalMark>
              {goalName || goalTypeLabel[goalType]}
              {numOfGoals > 1 && <CycleIcon />}
            </ChipButton>
          )}
          <ChipButton
            onClick={() => setIsInfoVisible(true)}
            aria-label={i18n.t(Translations.ProjectionInfoTitle)}
          >
            {i18n.t(Translations.HomeRateOfReturn, { rate: interest })}
          </ChipButton>
        </Chips>
      </HeaderRow>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <OnTrackLabel>
          {beneficiary?.type === BeneficiaryType.Self
            ? i18n.t(Translations.HomeOnTrackSelf)
            : i18n.t(Translations.HomeOnTrackFor, { name })}
        </OnTrackLabel>
        <HeadlineRow>
          <Headline>{formatCurrency(projectedAmount)}</Headline>
        </HeadlineRow>
      </div>

      <ChartBlock>
        {chartPath && (
          <svg
            width="100%"
            height={CHART_H}
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <defs>
              <linearGradient id="fmHomeReturns" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: home.copper }} stopOpacity="0.44" />
                <stop offset="100%" style={{ stopColor: home.copper }} stopOpacity="0.03" />
              </linearGradient>
            </defs>
            {GRID_LINES.map((y) => (
              <line
                key={y}
                x1="0"
                y1={y}
                x2={CHART_W}
                y2={y}
                style={{ stroke: home.line05 }}
                strokeWidth="1"
              />
            ))}
            <path d={chartPath.area} fill="url(#fmHomeReturns)" />
            <path
              d={chartPath.line}
              fill="none"
              style={{ stroke: home.copper }}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <AxisChip>{i18n.t(Translations.HomeToday)}</AxisChip>
          <ChartLegend>
            <span className="item">
              <span className="dot invested" />
              {i18n.t(Translations.HomeYouPutIn)} <b>{formatCurrency(investedAmount)}</b>
            </span>
            <span className="item time">
              <span className="dot returns" />
              {i18n.t(Translations.HomeTimeAdds)} <b>{formatCurrency(timeAdds)}</b>
            </span>
          </ChartLegend>
          <AxisChip>{Number.isFinite(targetYear) ? targetYear : ""}</AxisChip>
        </div>
      </ChartBlock>
    </Card>
  );
};
